import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { fileUrl } from "@/lib/api";
import { IMAGES } from "@/data/site";
import { PageHero } from "@/components/PageHero";
import { Editable, EditableImage } from "@/components/Editable";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import ProjectsMap from "@/components/ProjectsMap";
import { Building2, Users, Target, Compass, Network, FolderKanban, MapPin, ArrowUpRight } from "lucide-react";

const AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Crect width='300' height='300' fill='%23015E5C'/%3E%3Ccircle cx='150' cy='120' r='55' fill='%230B3B39'/%3E%3Crect x='70' y='195' width='160' height='120' rx='60' fill='%230B3B39'/%3E%3C/svg%3E";

function SectionTitle({ icon: Icon, eyebrow, id, defaultTitle, dark = false }) {
  return (
    <div className="mb-8">
      <div className="oj-eyebrow mb-3"><Icon className="w-4 h-4" /> {eyebrow}</div>
      <Editable as="h2" id={`about.${id}.title`} className={`${dark ? "text-3xl sm:text-4xl font-extrabold text-white" : "oj-h2"} block`}>{defaultTitle}</Editable>
    </div>
  );
}

export default function About() {
  const { lang, content, t } = useApp();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const stats = content.stats || {};
  const a = t.aboutP;

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const statKeys = ["solar_capacity", "co2_offset", "projects", "years"];

  return (
    <div>
      <PageHero image={IMAGES.building} eyebrow={a.whoWeAre} title={a.title} subtitle={a.sub} />

      {/* About Company */}
      <Section sid="about.company" name="About Company" id="company" className="oj-section scroll-mt-24">
        <div className="oj-container grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionTitle icon={Building2} eyebrow={a.corpProfile} id="company" defaultTitle={a.aboutCompany} />
            <Editable as="p" id="about.company.body" className="text-slate-600 leading-relaxed block">
              {a.body}
            </Editable>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {statKeys.map((k) => (
                <div key={k} className="oj-card p-4 text-center">
                  <div className="text-xl font-extrabold gold-text font-mono">{stats[k] || "—"}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{a.statLabels[k]}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl overflow-hidden border border-teal-100 h-[360px]">
              <EditableImage id="about.company.image" src={IMAGES.building} alt="OOJJAS headquarters" className="w-full h-full" />
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Board Members */}
      <Section sid="about.board" name="Board Members" id="board" className="oj-section bg-[#E9F5F4]/50 border-y border-teal-100 scroll-mt-24">
        <div className="oj-container">
          <SectionTitle icon={Users} eyebrow={a.leadership} id="board" defaultTitle={a.boardTitle} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {a.board.map(([role, desc], i) => (
              <Reveal key={role} delay={(i % 3) * 0.07}>
                <div className="oj-card overflow-hidden h-full" data-testid={`board-member-${i}`}>
                  <div className="h-52 bg-teal-900">
                    <EditableImage id={`about.board.${i}.photo`} src={AVATAR} alt={role} className="w-full h-full" />
                  </div>
                  <div className="p-5">
                    <Editable as="h4" id={`about.board.${i}.name`} className="font-bold text-[#0B3B39] block">{a.boardMember} {i + 1}</Editable>
                    <Editable as="div" id={`about.board.${i}.role`} className="text-sm font-semibold text-teal-600 mt-0.5 block">{role}</Editable>
                    <Editable as="p" id={`about.board.${i}.desc`} className="text-sm text-slate-600 mt-2 leading-relaxed block">{desc}</Editable>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-6">{a.boardNote}</p>
        </div>
      </Section>

      {/* Projects */}
      <Section sid="about.projects" name="Projects" id="projects" className="oj-section scroll-mt-24">
        <div className="oj-container">
          <SectionTitle icon={FolderKanban} eyebrow={a.portfolioEyebrow} id="projects" defaultTitle={a.projectsTitle} />
          <div className="mb-8"><ProjectsMap height="420px" /></div>
          {projects.length === 0 ? (
            <div className="oj-card p-10 text-center text-slate-500">{a.projectsEmpty}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {projects.map((p, i) => (
                <Reveal key={p.id} delay={(i % 2) * 0.08}>
                  <div className="oj-card p-6" data-testid={`about-project-${p.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono uppercase bg-teal-50 text-teal-700 border border-teal-200 capitalize">{p.vertical}</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase capitalize ${p.status === "operational" ? "bg-teal-50 text-teal-700 border border-teal-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{p.status}</span>
                    </div>
                    <h4 className="font-bold text-[#0B3B39]">{p.name}</h4>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-teal-500" /> {p.location}</div>
                    {p.capacity && <div className="text-sm gold-text font-mono font-bold mt-1">{p.capacity}</div>}
                    {p.description && <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.description}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Vision & Mission */}
      <Section sid="about.vm" name="Vision & Mission" className="relative overflow-hidden bg-[#E9F5F4] scroll-mt-24">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-teal-300/20 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative oj-container oj-section grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div id="vision" className="scroll-mt-24">
            <Reveal>
              <div className="oj-card p-8 h-full transition-all duration-300 hover:-translate-y-1.5 hover:!border-amber-400/60 hover:shadow-2xl hover:shadow-teal-900/20" data-testid="vision-card">
                <div className="grid place-items-center w-12 h-12 rounded-xl bg-amber-100 border border-amber-300 mb-4"><Compass className="w-6 h-6 text-amber-600" /></div>
                <h2 className="text-2xl font-extrabold text-[#0B3B39] mb-3">{a.vision}</h2>
                <div className="h-1 w-16 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 mb-5" />
                <Editable as="p" id="about.vision.body" className="text-lg font-semibold text-[#0B3B39] leading-snug block">
                  {a.visionBody}
                </Editable>
                <Editable as="p" id="about.vision.body2" className="mt-4 text-slate-700 leading-relaxed block">
                  {a.visionBody2}
                </Editable>
              </div>
            </Reveal>
          </div>
          <div id="mission" className="scroll-mt-24">
            <Reveal delay={0.1}>
              <div className="oj-card p-8 h-full transition-all duration-300 hover:-translate-y-1.5 hover:!border-amber-400/60 hover:shadow-2xl hover:shadow-teal-900/20" data-testid="mission-card">
                <div className="grid place-items-center w-12 h-12 rounded-xl bg-amber-100 border border-amber-300 mb-4"><Target className="w-6 h-6 text-amber-600" /></div>
                <h2 className="text-2xl font-extrabold text-[#0B3B39] mb-3">{a.mission}</h2>
                <div className="h-1 w-16 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 mb-5" />
                <div className="relative pl-6 border-l-2 border-amber-400">
                  <span className="absolute -left-1 -top-3 text-4xl text-amber-400 font-serif leading-none select-none" aria-hidden="true">&ldquo;</span>
                  <Editable as="p" id="about.mission.body" className="text-lg font-semibold text-[#0B3B39] leading-relaxed italic block">
                    {a.missionBody}
                  </Editable>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Organization Hierarchy */}
      <Section sid="about.hierarchy" name="Organization Hierarchy" id="hierarchy" className="relative overflow-hidden oj-section scroll-mt-24 bg-white border-t border-teal-100">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 flex items-center gap-2" aria-hidden="true">
          <span className="w-16 h-px bg-gradient-to-r from-transparent to-amber-400/70" />
          <span className="w-2 h-2 rotate-45 bg-amber-400" />
          <span className="w-16 h-px bg-gradient-to-l from-transparent to-amber-400/70" />
        </div>
        <div className="absolute -top-32 right-1/4 w-80 h-80 rounded-full bg-amber-300/15 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative oj-container">
          <SectionTitle icon={Network} eyebrow={a.structure} id="hierarchy" defaultTitle={a.hierarchyTitle} />
          <div className="max-w-3xl mx-auto space-y-3">
            {a.hierarchy.map(([title, desc], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="relative">
                  <div className="oj-card p-5 flex items-center gap-4 transition-all duration-300 hover:!border-amber-400/60 hover:shadow-lg hover:shadow-teal-900/10 hover:translate-x-1 ml-0 sm:ml-[var(--ml)]" style={{ "--ml": `${i * 6}%` }} data-testid={`hierarchy-level-${i}`}>
                    <div className="grid place-items-center w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-[#014845] font-extrabold text-sm shrink-0">{i + 1}</div>
                    <div>
                      <Editable as="h4" id={`about.hierarchy.${i}.title`} className="font-bold text-[#0B3B39] block">{title}</Editable>
                      <Editable as="div" id={`about.hierarchy.${i}.desc`} className="text-sm text-slate-700 block">{desc}</Editable>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/contact" className="oj-btn-gold" data-testid="about-contact-btn">{a.connect} <ArrowUpRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
